import { useState, useEffect } from 'react'
import { PlusIcon, UserIcon, XIcon } from '../icons'
import type { KanbanBoard, PublicUser, WorkspaceMember } from '../types'
import { API_URL, getAuthHeaders } from '../api'
import { useAuth } from '../context/AuthContext'
import { avatarUrl as getAvatarUrl } from '../utils/avatar'

export function EditBoardMembersModal({ board, workspaceId, onClose }: { board: KanbanBoard; workspaceId?: string; onClose: () => void }) {
  const [members, setMembers] = useState<WorkspaceMember[]>([])
  const [allUsersList, setAllUsersList] = useState<PublicUser[]>([])
  const { user: currentUser } = useAuth()
  const authHeaders = getAuthHeaders()

  const loadMembers = () => {
    fetch(`${API_URL}/api/workspaces/${workspaceId}/boards/${board.id}/members`, { headers: authHeaders })
      .then(r => r.json())
      .then(data => setMembers(data.members || []))
      .catch(() => {})
  }

  useEffect(() => {
    loadMembers()
    fetch(`${API_URL}/api/users`, { headers: authHeaders })
      .then(r => r.json())
      .then(data => setAllUsersList(data.users || []))
      .catch(() => {})
  }, [board.id])

  const handleAddMember = async (userId: string) => {
    try {
      await fetch(`${API_URL}/api/workspaces/${workspaceId}/boards/${board.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ user_id: userId }),
      })
      loadMembers()
    } catch { /* ignore */ }
  }

  const handleRemoveMember = async (userId: string) => {
    try {
      await fetch(`${API_URL}/api/workspaces/${workspaceId}/boards/${board.id}/members?user_id=${userId}`, {
        method: 'DELETE',
        headers: authHeaders,
      })
      loadMembers()
    } catch { /* ignore */ }
  }

  const memberIds = new Set(members.map(m => m.id))
  const nonMembers = allUsersList.filter(u => !memberIds.has(u.id))

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{board.name} — Membros</h2>
          <button className="icon-btn" onClick={onClose}><XIcon size={18} /></button>
        </div>

        <div className="profile-modal-form">
          <div className="profile-field">
            <label>Membros ({members.length})</label>
            <div className="ws-members-list">
              {members.map(m => (
                <div key={m.id} className="ws-member-row">
                  {m.avatar_url ? (
                    <img src={getAvatarUrl(m)} alt="" className="avatar-img" style={{ width: 24, height: 24, borderRadius: '50%' }} />
                  ) : (
                    <span className="ws-member-avatar"><UserIcon size={14} /></span>
                  )}
                  <span className="ws-member-name">{m.display_name || m.name || m.email}</span>
                  {m.id !== currentUser?.id && (
                    <button className="ws-member-remove" onClick={() => handleRemoveMember(m.id!)} title="Remover">
                      <XIcon size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {nonMembers.length > 0 && (
            <div className="profile-field">
              <label>Adicionar membros</label>
              <div className="ws-members-list">
                {nonMembers.map(u => (
                  <div key={u.id} className="ws-member-row ws-member-add">
                    {u.avatar_url ? (
                      <img src={getAvatarUrl(u)} alt="" className="avatar-img" style={{ width: 24, height: 24, borderRadius: '50%' }} />
                    ) : (
                      <span className="ws-member-avatar"><UserIcon size={14} /></span>
                    )}
                    <span className="ws-member-name">{u.display_name || u.name || u.email}</span>
                    <button className="ws-member-add-btn" onClick={() => handleAddMember(u.id)} title="Adicionar">
                      <PlusIcon size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="profile-modal-actions">
            <button type="button" className="btn-ghost" onClick={onClose}>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// BroadcastModal — send the same direct message to every recipient at once.
